import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Content } from "../../components/layout/Content";
import { Heading } from "../../components/layout/Heading";
import { Layout } from "../../components/layout/Layout";
import { SplashOverlay } from "../../components/layout/SplashOverlay";
import { fetchKeys } from "../../data/data";
import { About } from "./About";
import { Clients } from "./Clients";
import { Contact } from "./Contact";
import { Education } from "./Education";
import { Experience } from "./Experience";
import { Projects } from "./Projects";
import { Work } from "./Work";

export const AppContext = createContext<{
  keys: any | null;
  isMobile: boolean;
  scrollY: number | null;
  screenHeight: number | null;
  markSectionLoaded: (key: string) => void;
}>({
  keys: null,
  isMobile: false,
  scrollY: null,
  screenHeight: null,
  markSectionLoaded: () => {},
});

export const Profile = () => {
  const [keys, setKeys] = useState<any | null>({});
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [scrollY, setScrollY] = useState<number | null>(null);
  const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
  const [loadedSections, setLoadedSections] = useState<Record<string, boolean>>(
    {}
  );
  const [splashState, setSplashState] = useState<
    "visible" | "fading" | "hidden"
  >("visible");
  const splashStartRef = useRef<number>(Date.now());

  const sectionKeys = useMemo(
    () => [
      "keys",
      "about",
      "experience",
      "work",
      "education",
      "projects",
      "clients",
      "contact",
    ],
    []
  );

  const markSectionLoaded = useCallback((key: string) => {
    setLoadedSections((prev) => {
      if (prev[key]) {
        return prev;
      }
      return {
        ...prev,
        [key]: true,
      };
    });
  }, []);

  const allLoaded = useMemo(
    () => sectionKeys.every((key) => loadedSections[key]),
    [loadedSections, sectionKeys]
  );

  useEffect(() => {
    handleResize();
    fetchKeys().then((response) => {
      setKeys(
        response.reduce((agg, row) => {
          agg = {
            ...agg,
            [row[0]]: row[1],
          };
          return agg;
        }, {})
      );
      markSectionLoaded("keys");
    });
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize, false);
      window.addEventListener("scroll", handleScroll, false);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize, false);
        window.removeEventListener("scroll", handleScroll, false);
      }
    };
  }, []);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 600);
    setScreenHeight(window.innerHeight);
  };

  const handleScroll: EventListener = (event: Event) => {
    setScrollY(window.scrollY);
  };

  useEffect(() => {
    const splashMinDuration = 1000;
    if (allLoaded && splashState === "visible") {
      const elapsed = Date.now() - splashStartRef.current;
      const remaining = Math.max(0, splashMinDuration - elapsed);
      const timer = window.setTimeout(() => {
        setSplashState("fading");
      }, remaining);
      return () => {
        window.clearTimeout(timer);
      };
    }
    return undefined;
  }, [allLoaded, splashState]);

  useEffect(() => {
    if (splashState !== "fading") {
      return;
    }
    const timer = window.setTimeout(() => {
      setSplashState("hidden");
    }, 320);
    return () => {
      window.clearTimeout(timer);
    };
  }, [splashState]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    if (splashState === "hidden") {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [splashState]);

  return (
    <AppContext.Provider
      value={{ keys, isMobile, scrollY, screenHeight, markSectionLoaded }}
    >
      <Layout>
        {splashState !== "hidden" && (
          <SplashOverlay state={splashState} />
        )}
        <Heading />
        <Content>
          <About />
          <Experience />
          <Work />
          <Projects />
          <Education />
          <Clients />
          <Contact />
        </Content>
      </Layout>
    </AppContext.Provider>
  );
};
